import API from "../../utils/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import TokenService from "../../utils/token-service";
import { normalizeClientDetailCategory } from "../../utils/clientDetailsCategory";

const findClientInList = (list, clientId) => {
  if (!clientId || !Array.isArray(list)) return null;
  return (
    list.find((c) => String(c?.client_id) === String(clientId)) || null
  );
};

/** Fallback when GET client/getClientById is missing (404) or client not in detail route. */
const fetchClientFromListApis = async (clientId) => {
  const id = encodeURIComponent(clientId);
  const role = TokenService.getUserRole();

  const listUrls = [];
  if (role === "employee" || role === "manager") {
    listUrls.push(`client/getAllClient?overAll=true&scope=all`);
    listUrls.push(`client/getAllClient?overAll=true${getAssignedBy("&")}`);
  } else {
    listUrls.push("client/getAllClient?overAll=true");
    listUrls.push("client/getAllClient?active=true");
  }

  for (const url of listUrls) {
    try {
      const { data } = await API.get(url);
      const match = findClientInList(data?.data, clientId);
      if (match) return match;
    } catch {
      /* try next list endpoint */
    }
  }

  try {
    const { data } = await API.post("client/getSearchClient", {
      client_id: clientId,
      scope: "all",
    });
    return findClientInList(data?.data, clientId);
  } catch {
    /* search unavailable */
  }

  return null;
};

const getClientById = createAsyncThunk(
  "client/getClientById",
  async (clientId) => {
    if (!clientId) return null;

    const safeId = encodeURIComponent(clientId);

    try {
      const { data } = await API.get(`client/getClientById/${safeId}`);
      if (data?.data) return data.data;
    } catch (error) {
      const status = error?.response?.status;
      if (status && status !== 404) {
        console.warn("getClientById failed:", status, error?.response?.data);
      }
    }

    const fromList = await fetchClientFromListApis(clientId);
    if (fromList) return fromList;

    console.warn(
      `getClientById: no client found for id ${clientId} (detail API 404 and list fallback empty)`,
    );
    return null;
  },
);

const getAssignedBy = (seperator) => {
  return (TokenService.getUserRole() === "employee" || TokenService.getUserRole() === "manager")
    ? (seperator ? seperator : "?") + "assigned_by=" + TokenService.getUserID()
    : "";
};
/** Public consultant lookup for client self-signup (no auth required). */
const searchSignupConsultants = async (search = "") => {
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : "";
    const {
      data: { data },
    } = await API.get(`client/searchSignupConsultants${query}`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

const insertUpdateClient = async (state) => {
  try {
    const { params, insertUpdateFlag, client_id } = state;

    const API_NAME =
      insertUpdateFlag === 0
        ? "client/createClient"
        : `client/updateClientById/${client_id}`;
    const data = await API.post(`${API_NAME}`, params);
    return data;
  } catch (error) {
    return [];
  }
};

const handleActivateClients = async (selected_clients, user_email) => {
  try {
   
   console.log("INSIDE CLIENT ACTION CLIENTS TO ACTIVATE: ",selected_clients);
   console.log("INSIDE CLIENT ACTION CLIENTS TO ACTIVATE: ",user_email);
    const data = await API.post(`client/activateClients/${user_email}`, selected_clients);
    return data;
  } catch (error) {
    return [];
  }
};
const getActiveClients = createAsyncThunk(
  "client/getActiveClientsStatus",
  async (filters = {}) => {
    try {
      let clientMaster = [];
      const scopeAll = filters?.scope === "all";
      let url = "client/getAllClient?active=true";
      if (scopeAll) {
        url += "&scope=all";
      }
      const {
        data: { data },
      } = await API.get(url);
      if (data?.length > 0) {
        clientMaster = data.map(({ client_id, first_name, last_name }) => {
          return { id: client_id, value: `${first_name} ${last_name}` };
        });
      }
      return { data, clientMaster };
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

const getDashboardCardDetails = createAsyncThunk(
  "client/getDashboardCardDetailsStatus",
  async ({ user_id, category } = {}) => {
    try {
      const parts = [];
      if (user_id) parts.push(`user_id=${encodeURIComponent(user_id)}`);
      if (category) {
        parts.push(
          `category=${encodeURIComponent(normalizeClientDetailCategory(category))}`,
        );
      }
      const url = `client/dashboardCardDetails${
        parts.length ? `?${parts.join("&")}` : ""
      }${getAssignedBy(parts.length ? "&" : "?")}`;

      const {
        data: { data },
      } = await API.get(url);
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

// const getAllClients = createAsyncThunk(
//   "client/getAllClientsStatus",
//   async () => {
//     try {
//       const {
//         data: { data },
//       } = await API.get(
//         `client/getAllClient?overAll=true${getAssignedBy("&")}`
//       );
//       return data;
//     } catch (error) {
//       console.log(error);
//       return [];
//     }
//   }
// );

const getAllClients = createAsyncThunk(
  "client/getAllClientsStatus",
  async (filters) => {
    try {
      // Employee/manager: scope "assigned" (default) adds assigned_by; scope "all" = full list (used during search)
      const scopeAll = filters?.scope === "all";
      const appendAssigned =
        !scopeAll &&
        !filters?.signup_requests &&
        (TokenService.getUserRole() === "employee" ||
          TokenService.getUserRole() === "manager");

      const query = new URLSearchParams();
      if (filters?.signup_requests) {
        query.append("signup_requests", "true");
        query.append("include_services", "true");
        if (filters?.client_type) {
          query.append("client_type", String(filters.client_type));
        }
        if (filters?.service_category) {
          query.append(
            "service_category",
            String(filters.service_category).trim().toLowerCase(),
          );
        }
      } else {
        query.append("overAll", "true");
      }
      if (filters?.manager_id) {
        query.append("manager_id", String(filters.manager_id));
      }
      if (filters?.include_subordinate_tree) {
        query.append(
          "include_subordinate_tree",
          String(filters.include_subordinate_tree),
        );
      }

      let path = `client/getAllClient?${query.toString()}`;
      if (appendAssigned) {
        path += getAssignedBy("&");
      }

      const { data } = await API.get(path);
      return data.data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);


const getSearchClient = createAsyncThunk(
  "client/getSearchClientStatus",
  async (filters = {}) => {
    try {
      // For employee/manager: scope "all" = no assigned_by (search across all clients);
      // otherwise only search within assigned clients
      const scopeAll = filters?.scope === "all";
      const appendAssigned =
        !scopeAll &&
        !filters?.signup_requests &&
        (TokenService.getUserRole() === "employee" ||
          TokenService.getUserRole() === "manager");

      const url = `client/getSearchClient${appendAssigned ? getAssignedBy("?") : ""}`;

      const payload = { ...filters };
      if (payload.signup_requests) {
        payload.include_services = true;
      }

      const {
        data: { data },
      } = await API.post(url, payload);
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);
// for reports
const getOverAllClients = createAsyncThunk(
  "client/getOverAllClientsStatus",
  async (filters = {}) => {
    try {
      const { start_date, end_date } = filters;
      
      const query = new URLSearchParams();
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);
      
      const { data: { data } } = await API.get(`client/getAllClient?${query.toString()}`);
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);


const getIndividualClients = createAsyncThunk(
  "client/getIndividualClientsStatus",
  async (filters = {}) => {
    try {
      const { start_date, end_date } = filters;

      const query = new URLSearchParams();
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);
      query.append("individual", true);

      const { data: { data } } = await API.get(`client/getAllClient?${query.toString()}`);
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

const getBusinessClients = createAsyncThunk(
  "client/getBusinessClientsStatus",
  async (filters = {}) => {
    try {
      const { start_date, end_date } = filters;

      const query = new URLSearchParams();
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);
      query.append("business", true);

      const { data: { data } } = await API.get(`client/getAllClient?${query.toString()}`);
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

// Client Comments Actions
const getClientComments = createAsyncThunk(
  "client/getClientCommentsStatus",
  async (arg) => {
    try {
      const clientId =
        typeof arg === "object" && arg !== null ? arg.clientId : arg;
      const { start_date, end_date, category } =
        typeof arg === "object" && arg !== null ? arg : {};

      const query = new URLSearchParams();
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);
      if (category) query.append("category", category);

      const qs = query.toString();
      const { data } = await API.get(
        `client/getClientComments/${clientId}${qs ? `?${qs}` : ""}`,
      );
      return data.data || [];
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

const createClientComment = async (state) => {
  try {
    const { client_id, comment, category = "individual" } = state;
    const data = await API.post(`client/createClientComment`, {
      client_id,
      comment,
      category,
    });
    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const deleteClientComment = async (commentId) => {
  try {
    const data = await API.delete(`client/deleteClientComment/${commentId}`);
    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const updateClientComment = async (state) => {
  try {
    const { comment_id, comment } = state || {};
    const data = await API.put(`client/updateClientComment/${comment_id}`, {
      comment,
    });
    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

/**
 * Client transfer (Admin/Manager)
 * Backend endpoints are defined in docs/CLIENT_TRANSFER_BACKEND_PROMPT.md
 */
const getClientsAssignedToEmployee = async (employee_id) => {
  try {
    const { data } = await API.get(
      `client/getClientsAssignedToEmployee?employee_id=${encodeURIComponent(
        employee_id,
      )}`,
    );
    return data?.data || [];
  } catch (error) {
    console.log(error);
    return [];
  }
};

const transferClientsToEmployee = async ({
  from_employee_id,
  to_employee_id,
  client_ids,
}) => {
  try {
    const { data } = await API.post(`client/transferClientsToEmployee`, {
      from_employee_id,
      to_employee_id,
      client_ids,
    });
    return data;
  } catch (error) {
    console.log(error);
    return { status: 0, message: "Transfer failed" };
  }
};

const getClientBusinesses = async (clientId) => {
  try {
    const { data } = await API.get(
      `client/getClientBusinesses/${encodeURIComponent(clientId)}`,
    );
    if (data?.status === 1 && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error) {
    console.warn("getClientBusinesses failed:", error);
    return [];
  }
};

const getAllBusinesses = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    if (params.search) query.set("search", String(params.search).trim());
    const qs = query.toString() ? `?${query.toString()}` : "";
    const { data } = await API.get(`client/getAllBusinesses${qs}`);
    if (data?.status === 1 && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error) {
    console.warn("getAllBusinesses failed:", error);
    return [];
  }
};

const getBusinessMembers = async (clientBusinessId) => {
  try {
    const { data } = await API.get(
      `client/getBusinessMembers/${encodeURIComponent(clientBusinessId)}`,
    );
    if (data?.status === 1 && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error) {
    console.warn("getBusinessMembers failed:", error);
    return [];
  }
};

const getAssignableBusinessClients = async (clientBusinessId, search = "") => {
  try {
    const query = new URLSearchParams();
    if (search) query.set("search", String(search).trim());
    const qs = query.toString() ? `?${query.toString()}` : "";
    const { data } = await API.get(
      `client/getAssignableBusinessClients/${encodeURIComponent(
        clientBusinessId,
      )}${qs}`,
    );
    if (data?.status === 1 && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error) {
    console.warn("getAssignableBusinessClients failed:", error);
    return [];
  }
};

const assignBusinessMembers = async ({ client_business_id, client_ids }) => {
  try {
    const { data } = await API.post("client/assignBusinessMembers", {
      client_business_id,
      client_ids,
    });
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const getMyBusinessAccess = async () => {
  try {
    const { data } = await API.get("client/getMyBusinessAccess");
    return data;
  } catch (error) {
    return (
      error?.response?.data || {
        status: 0,
        message: "Could not load your businesses.",
      }
    );
  }
};

const addBusinessMemberByEmail = async ({ client_business_id, email }) => {
  try {
    const { data } = await API.post("client/addBusinessMemberByEmail", {
      client_business_id,
      email,
    });
    return data;
  } catch (error) {
    return {
      ...(error?.response?.data || { status: 0, message: "Request failed" }),
      httpStatus: error?.response?.status,
    };
  }
};

const removeBusinessMember = async ({ client_business_id, client_id }) => {
  try {
    const { data } = await API.delete("client/removeBusinessMember", {
      data: { client_business_id, client_id },
    });
    return data;
  } catch (error) {
    return {
      ...(error?.response?.data || { status: 0, message: "Request failed" }),
      httpStatus: error?.response?.status,
    };
  }
};

const createClientBusiness = async (payload) => {
  try {
    const { data } = await API.post("client/createClientBusiness", payload);
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const updateClientBusiness = async (clientBusinessId, payload) => {
  try {
    const { data } = await API.put(
      `client/updateClientBusiness/${encodeURIComponent(clientBusinessId)}`,
      payload,
    );
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const deleteClientBusiness = async (clientBusinessId, clientId) => {
  try {
    const { data } = await API.delete(
      `client/deleteClientBusiness/${encodeURIComponent(clientBusinessId)}?client_id=${encodeURIComponent(clientId)}`,
    );
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const setPrimaryClientBusiness = async (clientBusinessId, clientId) => {
  try {
    const { data } = await API.patch(
      `client/setPrimaryClientBusiness/${encodeURIComponent(clientBusinessId)}`,
      { client_id: clientId },
    );
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const getClientServices = async ({ client_id, status, service_category } = {}) => {
  try {
    const params = { client_id };
    if (status) params.status = status;
    if (service_category) {
      params.service_category = String(service_category).trim().toLowerCase();
    }
    const { data } = await API.get("client/getClientServices", { params });
    if (data?.status === 1 && Array.isArray(data.data)) return data.data;
    return [];
  } catch (error) {
    console.warn("getClientServices failed:", error);
    return [];
  }
};

/**
 * Dashboard pending/ongoing services for the logged-in staff user.
 * Scope is enforced by backend:
 * - employee: services for their assigned clients
 * - manager: own clients + subordinates' clients
 * - admin: all pending services (optional)
 *
 * Expected response:
 * { status: 1, data: { count: number, services: ServiceRow[] } }
 * or { status: 1, data: ServiceRow[] }
 */
const getDashboardPendingServices = async ({
  status,
  service_category,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (status) params.set("status", String(status).trim().toLowerCase());
    if (service_category) {
      params.set(
        "service_category",
        String(service_category).trim().toLowerCase(),
      );
    }
    const qs = params.toString();
    const url = qs
      ? `client/getDashboardPendingServices?${qs}`
      : "client/getDashboardPendingServices";
    const { data } = await API.get(url);
    if (data?.status !== 1) {
      return { count: 0, services: [] };
    }
    const payload = data?.data;
    if (Array.isArray(payload)) {
      return { count: payload.length, services: payload };
    }
    if (payload && typeof payload === "object") {
      const services = Array.isArray(payload.services) ? payload.services : [];
      const count =
        payload.count != null ? Number(payload.count) || 0 : services.length;
      return { count, services };
    }
    return { count: 0, services: [] };
  } catch (error) {
    console.warn("getDashboardPendingServices failed:", error);
    return { count: 0, services: [] };
  }
};

/**
 * Summary counts for the Services dashboard card.
 * Returns { pending: number, completed: number }.
 * Falls back gracefully if the backend doesn't support the status param.
 */
const getDashboardServicesCounts = async () => {
  try {
    const [ongoingRes, closedRes] = await Promise.all([
      getDashboardPendingServices({ status: "ongoing" }),
      getDashboardPendingServices({ status: "closed" }),
    ]);
    return {
      pending: ongoingRes.count,
      completed: closedRes.count,
    };
  } catch (error) {
    console.warn("getDashboardServicesCounts failed:", error);
    return { pending: 0, completed: 0 };
  }
};

/**
 * Batch-load services for many clients (New Clients Request page).
 * Preferred response: { status: 1, data: { [client_id]: Service[] } }
 * Also accepts: { status: 1, data: [{ client_id, services: [] }] }
 * Returns null when the endpoint is unavailable so callers can fall back.
 */
const getClientServicesByClientIds = async ({
  client_ids = [],
  service_category,
} = {}) => {
  const ids = (client_ids || []).map((id) => String(id)).filter(Boolean);
  if (!ids.length) return {};
  try {
    const payload = { client_ids: ids };
    if (service_category) {
      payload.service_category = String(service_category).trim().toLowerCase();
    }
    const { data } = await API.post(
      "client/getClientServicesByClientIds",
      payload,
    );
    if (data?.status !== 1 || data?.data == null) return null;

    if (!Array.isArray(data.data) && typeof data.data === "object") {
      const map = {};
      Object.entries(data.data).forEach(([clientId, services]) => {
        map[String(clientId)] = Array.isArray(services) ? services : [];
      });
      return map;
    }

    if (Array.isArray(data.data)) {
      const map = {};
      data.data.forEach((row) => {
        const clientId = String(row?.client_id ?? row?.clientId ?? "");
        if (!clientId) return;
        map[clientId] = Array.isArray(row.services)
          ? row.services
          : Array.isArray(row.client_services)
            ? row.client_services
            : [];
      });
      return map;
    }
    return null;
  } catch (error) {
    console.warn("getClientServicesByClientIds failed:", error);
    return null;
  }
};

const requestClientService = async (payload) => {
  try {
    const { data } = await API.post("client/requestClientService", payload);
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const updateClientServiceStatus = async (payload) => {
  try {
    const { data } = await API.patch("client/updateClientServiceStatus", payload);
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const getUnassignedFiledCopies = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && String(value).trim() !== "") {
        query.append(key, value);
      }
    });
    const { data } = await API.get(
      `client/getUnassignedFiledCopies?${query.toString()}`,
    );
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed", data: [] };
  }
};

const updateClientService = async (payload) => {
  try {
    const { data } = await API.patch("client/updateClientService", payload);
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

const completeServiceOnboarding = async (clientId) => {
  try {
    const { data } = await API.post("client/completeServiceOnboarding", {
      client_id: clientId,
    });
    return data;
  } catch (error) {
    return error?.response?.data || { status: 0, message: "Request failed" };
  }
};

export {
  searchSignupConsultants,
  insertUpdateClient,
  getActiveClients,
  getSearchClient,
  getAllClients,
  getDashboardCardDetails,
  getOverAllClients,
  getIndividualClients,
  getBusinessClients,
  handleActivateClients,
  getClientById,
  getClientComments,
  createClientComment,
  deleteClientComment,
  updateClientComment,
  getClientsAssignedToEmployee,
  transferClientsToEmployee,
  getClientBusinesses,
  getAllBusinesses,
  getBusinessMembers,
  getAssignableBusinessClients,
  assignBusinessMembers,
  getMyBusinessAccess,
  addBusinessMemberByEmail,
  removeBusinessMember,
  createClientBusiness,
  updateClientBusiness,
  deleteClientBusiness,
  setPrimaryClientBusiness,
  getClientServices,
  getClientServicesByClientIds,
  getDashboardPendingServices,
  getDashboardServicesCounts,
  requestClientService,
  updateClientServiceStatus,
  updateClientService,
  getUnassignedFiledCopies,
  completeServiceOnboarding,
};
